import CONSTANTS from "@/config/constants";
import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import { tokenRefreshManager } from "@/lib/tokenRefresh";
import { toast } from "sonner";

const API_BASE_URL = CONSTANTS.BASE_URL;

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

api.interceptors.request.use(
  async (config) => {
    // if (
    //   env.ENABLE_PAYLOAD_ENCRYPTION &&
    //   config.data &&
    //   !(config.data instanceof FormData)
    // ) {
    //   try {
    //     config.data = {
    //       encrypted: await ApiEncryptionService.encrypt(config.data),
    //     };
    //   } catch (error) {
    //     console.error("Request encryption error:", error);
    //     return Promise.reject(error);
    //   }
    // }

    if (
      config.url?.includes("/auth/login") ||
      config.url?.includes("/auth/register") ||
      config.url?.includes("/auth/refresh") ||
      config.url?.includes("/auth/change-required-password") ||
      config.url?.includes("/membership-plans")
    ) {
      return config;
    }

    try {
      const token = await tokenRefreshManager.getValidAccessToken();

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error("Error getting access token:", error);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

let lastRateLimitToastAt = 0;
const RATE_LIMIT_TOAST_COOLDOWN_MS = 5000;

// Some middleware (e.g. the express-rate-limit 429 handler) responds with a
// plain-text/HTML body instead of JSON, so `error.response.data` can be a
// raw string rather than `{ message: string }`. Normalize it here so every
// call site's existing `err.response?.data?.message` check keeps working.
const normalizeErrorResponseData = (error: AxiosError) => {
  const data = error.response?.data;
  if (typeof data === "string" && data.trim()) {
    (error.response as any).data = { message: data.trim() };
  }
};

const showRateLimitToast = (error: AxiosError) => {
  const now = Date.now();
  if (now - lastRateLimitToastAt < RATE_LIMIT_TOAST_COOLDOWN_MS) {
    return;
  }
  lastRateLimitToastAt = now;

  const responseData = error.response?.data as { message?: string } | undefined;
  const retryAfter = error.response?.headers?.["retry-after"];
  const retrySeconds = retryAfter ? Number(retryAfter) : undefined;

  const message =
    responseData?.message ||
    (retrySeconds
      ? `Too many requests. Please try again in ${retrySeconds} second${retrySeconds === 1 ? "" : "s"}.`
      : "Too many requests. Please try again later.");

  toast.error(message);
};

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    normalizeErrorResponseData(error);

    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const newAccessToken = await tokenRefreshManager.refreshAccessToken();

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }
        return api(originalRequest);
      } catch {
        return Promise.reject(error);
      }
    }

    // Only auto-toast for read (GET) requests. Mutating calls (login, save,
    // delete, ...) already surface `err.response.data.message` via their own
    // catch block, so auto-toasting here would just duplicate that message.
    if (error.response?.status === 429 && originalRequest.method?.toLowerCase() === "get") {
      showRateLimitToast(error);
    }

    return Promise.reject(error);
  }
);



export const twoFactorAPI = {
  setup: () => api.post("/auth/2fa/setup"),

  verify: (token: string) => api.post("/auth/2fa/verify", { token }),

  disable: (password: string) => api.post("/auth/2fa/disable", { password }),

  regenerateBackupCodes: () => api.post("/auth/2fa/regenerate-backup-codes"),

  getStatus: () => api.get("/auth/2fa/status"),
};

export const emailAccountAPI = {
  getAll: () => api.get("/email-accounts"),

  create: (accountData: {
    email: string;
    provider: string;
    imapHost?: string;
    imapPort?: number;
    smtpHost?: string;
    smtpPort?: number;
    username?: string;
    password?: string;
    useSSL?: boolean;
    useTLS?: boolean;
  }) => api.post("/email-accounts", accountData),

  update: (id: string, accountData: any) =>
    api.put(`/email-accounts/${id}`, accountData),

  delete: (id: string) => api.delete(`/email-accounts/${id}`),

  testConnection: (id: string) => api.post(`/email-accounts/${id}/test`),

  setDefault: (id: string) => api.post(`/email-accounts/${id}/set-default`),

  autoDiscover: (email: string) =>
    api.post("/email-accounts/auto-discover", { email }),
};

export const emailRuleAPI = {
  getAll: () => api.get("/email-rules"),

  create: (ruleData: {
    name: string;
    conditions: Array<{
      field: string;
      operator: string;
      value: string;
    }>;
    actions: Array<{
      type: string;
      value?: string;
    }>;
    priority?: number;
  }) => api.post("/email-rules", ruleData),

  update: (id: string, ruleData: any) =>
    api.put(`/email-rules/${id}`, ruleData),

  delete: (id: string) => api.delete(`/email-rules/${id}`),

  test: (id: string, emailData: any) =>
    api.post(`/email-rules/${id}/test`, emailData),

  apply: (id: string, emailIds: string[]) =>
    api.post(`/email-rules/${id}/apply`, { emailIds }),

  toggleStatus: (id: string) => api.post(`/email-rules/${id}/toggle`),
};

// User API
export const userAPI = {
  getProfile: () => api.get("/user/profile"),

  updateProfile: (profileData: {
    firstName?: string;
    lastName?: string;
    displayName?: string;
    timezone?: string;
    language?: string;
  }) => api.put("/user/profile", profileData),

  updatePreferences: (preferences: {
    emailsPerPage?: number;
    autoRefresh?: boolean;
    showPreview?: boolean;
    markAsReadOnView?: boolean;
    enableNotifications?: boolean;
    theme?: string;
  }) => api.put("/user/preferences", preferences),

  changePassword: (currentPassword: string, newPassword: string) =>
    api.put("/user/change-password", { currentPassword, newPassword }),

  deleteAccount: (password: string) =>
    api.delete("/user/account", { data: { password } }),
};

export default api;
